// external imports
import { isNil } from 'lodash';
import React from 'react';

// Custom Hooks
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { isBlurEvent } from '@makemydeal/dr-shared-ui-utils';

// interstate components
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { GetError } from '@interstate/components/Validation';

import { constants as offerReduxConstants, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';

// utils
import { formatValue } from '../../utils/formatUtils';

// CSS style components
import { leftAlignColumnStyle, rightAlignColumnStyle } from '../RollToStyles';

// types
import type { Row } from '../../types/RollToPaymentOptionRow';
import { inputFieldRightAlign } from '../RollToOptions.interstate.style';

type AdjustCellProps = {
    row: Row;
    rowIndex: number;
    getAdjustedFieldInputValue: (index: number, event: InterstateOnChangeEvent<TextInputEventValue>) => void;
};

const getAdjustedValue = (adjustedRowValue: number | string | null | undefined): number | string | null => {
    if (typeof adjustedRowValue === 'number') {
        return adjustedRowValue;
    }

    if (typeof adjustedRowValue === 'string') {
        const parsed = parseFloat(adjustedRowValue);
        return !isNaN(parsed) ? parsed : adjustedRowValue;
    }

    return null;
};

const MONEY_FACTOR_MAX_ERROR = `Cannot exceed ${offerReduxConstants.MAX_MONEY_FACTOR}`;

const moneyFactorGetError: GetError = (_label, value) =>
    offerReduxUtils.exceedsMoneyFactorBound(parseFloat(String(value)))
        ? { triggered: true, validationState: { isValid: false, type: 'error', message: MONEY_FACTOR_MAX_ERROR } }
        : { triggered: true, validationState: { isValid: true, type: undefined } };

const isMoneyFactorRow = (row: Row): boolean => row.isMoneyFactorRate === true;

const AdjustCell: React.FC<AdjustCellProps> = ({ row, rowIndex, getAdjustedFieldInputValue }) => {
    const adjust = getAdjustedValue(row.adjust);
    const isEmpty = isNil(adjust);

    const handleChange = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isBlurEvent(event)) {
            return;
        }
        getAdjustedFieldInputValue(rowIndex, event);
    };

    if (isEmpty) {
        return <div style={leftAlignColumnStyle}></div>;
    }

    if (row.isAdjustableField) {
        return (
            <div style={leftAlignColumnStyle} data-testid={`adjust-cell-${row.label}`}>
                <NumericInput
                    name={`adjust-cell-${row.label}`}
                    allowDecimal={true}
                    displayLabel={false}
                    autoInsertCommas={true}
                    inputPrefix={row.inputPrefix}
                    decimalMaxLength={5}
                    decimalMinLength={0}
                    onChange={handleChange}
                    value={adjust}
                    size="small"
                    sx={inputFieldRightAlign}
                    {...(isMoneyFactorRow(row) && { getError: moneyFactorGetError })}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                ...rightAlignColumnStyle,
                fontWeight: row.bold ? 'bold' : 'normal'
            }}
            data-testid={`adjust-cell-${row.label}`}
        >
            {formatValue(row.label, adjust)}
        </div>
    );
};

export default AdjustCell;
